import { Company, CompanyUserStatus } from '~/types/company';
import { Subscription } from '~/types/subscriptions';
import { UserPersonalData } from './user-personal-data';

export enum UserIdentificationStatus {
  PENDING = 'PENDING',
}

export interface User {
  id: string;
  createdAt: string;
  email: string;
  firstName: string;
  lastName: string;
  phone?: string;
  updatedAt: string;
  referralCode?: string;
  identificationStatus?: UserIdentificationStatus;
  isSwanUser: boolean;
  /** Derived: true once the user has completed at least one successful TOTP verification. */
  totpEnabled: boolean;
  /** ISO timestamp of when TOTP was enabled; present only while `totpEnabled` is true. */
  totpEnabledAt?: string;
  /** Whether the user is required to use 2FA. */
  mfaRequired: boolean;
  /** Consents returned by /me. The app is gated until the required ones (terms, privacy) are true. */
  termsAndConditionsAgreed?: boolean;
  privacyPolicyAgreed?: boolean;
  marketingAgreed?: boolean;
}

export interface UserCompany extends Company {
  subscriptions: Subscription[];
  userStatus: CompanyUserStatus;
}
export interface UserWithCompanies extends User {
  companies: UserCompany[];
  userPersonalData?: UserPersonalData;
}

export const UserQueryKeys = {
  currentUser: ['User'],
  notificationsToken: ['NotificationsToken'],
  invitedUser: (token: string) => ['InvitedUser', { token }],
  invitedUserAll: ['InvitedUser'],
  intercomToken: ['IntercomToken'],
};

/**
 * Second-factor channels the backend may offer at the login challenge, in
 * preference order (`passkey` > `totp` > `email_otp`). `passkey` is a full
 * first-factor login (WebAuthn) that satisfies MFA on its own — it has no
 * `mfa_token` challenge endpoint (see `MfaChallenge`).
 */
export type MfaMethod = 'passkey' | 'totp' | 'email_otp';

/** `POST /api/auth/login` — user without active MFA (unchanged shape). */
export interface LoginTokenResponse {
  token: string;
}

/**
 * `POST /api/auth/login` — user with active MFA (phase 2). No session/token is
 * issued yet: the login completes only after a challenge validated with the
 * short-lived `mfaToken` (Bearer, TTL 10 min).
 */
export interface LoginMfaChallengeResponse {
  mfaRequired: true;
  mfaToken: string;
  methods: MfaMethod[];
}

export type LoginResponse = LoginTokenResponse | LoginMfaChallengeResponse;

/** Discriminated result surfaced by the login mutation to the view (camelCase). */
export type LoginResult =
  | { status: 'authenticated' }
  | { status: 'mfa_required'; mfaToken: string; methods: MfaMethod[] };

export interface UserData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  phone?: {
    dialCode: string;
    number: string;
  };
  termsAndConditionsAgreed: boolean;
  privacyPolicyAgreed: boolean;
  marketingAgreed: boolean;
}
